<template lang="pug">
div
  v-row.monitorsys-row
    v-col(
      v-for="card in summaryCards",
      :key="card.key",
      cols="12",
      sm="6",
      md="3"
    )
      summary-card(
        :title="card.title",
        :value="card.value",
        :icon="card.icon",
        :variant="card.variant",
        :icon-variant="card.iconVariant"
      )

  v-row.monitorsys-row
    v-col(cols="12")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Solicitações por Cliente
          v-btn.panel-card__expand(
            outlined,
            small,
            class="monitorsys-action-button",
            @click="dialogs.chart = true"
          )
            v-icon(small) mdi-arrow-expand
        v-card-text.panel-card__content
          apexchart(
            v-if="clientesChartSeries.length",
            type="bar",
            :options="clientesChartOptions",
            :series="clientesChartSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

  v-row.monitorsys-row
    v-col(cols="12")
      v-card.panel-card.panel-card--details(outlined)
        .panel-card__header
          span.panel-card__title Detalhamento das solicitações
          v-text-field.monitorsys-search-field(
            dense,
            outlined,
            hide-details,
            clearable,
            placeholder="Buscar",
            prepend-inner-icon="mdi-magnify",
            v-model="search"
          )

        v-card-text.panel-card__content.panel-card__content--details
          .panel-card__empty(v-if="!filteredClientes.length")
            span Nenhum cliente encontrado.
          v-expansion-panels(v-else, multiple, v-model="expandedPanels", accordion)
            v-expansion-panel(
              v-for="cliente in filteredClientes",
              :key="cliente.nome"
            )
              v-expansion-panel-header
                .monitorsys-expansion-header
                  span.monitorsys-expansion-header__title {{ cliente.nome }}
                  v-chip(
                    small,
                    outlined,
                    color="primary",
                    class="monitorsys-expansion-header__badge"
                  ) {{ cliente.detalhes.length }}
              v-expansion-panel-content
                v-data-table(
                  :headers="detalhesHeaders",
                  :items="cliente.detalhes",
                  :items-per-page="5",
                  :footer-props="{ itemsPerPageOptions: [5, 10, 20] }",
                  dense,
                  class="monitorsys-details-table"
                )
                  template(v-slot:item.status="{ item }")
                    v-chip(
                      small,
                      outlined,
                      :color="statusChipColor(item.status)"
                    ) {{ statusLabel(item.status) }}
                  template(v-slot:item.ultimaInteracao="{ item }")
                    span {{ formatDateTime(item.ultimaInteracao) }}

  v-dialog(
    :value="dialogs.chart",
    @input="dialogs.chart = $event",
    content-class="monitorsys-dialog-content",
    max-width="1200px",
    width="1200px"
  )
    v-card.panel-dialog
      .panel-card__header.panel-card__header--dialog
        span.panel-card__title Solicitações por Cliente
        v-btn(icon, @click="dialogs.chart = false")
          v-icon mdi-close
      v-card-text.panel-card__dialog-content.panel-card__dialog-content--scroll.panel-card__dialog-content--chart
        .panel-card__chart-canvas(v-if="clientesDialogChartSeries.length", :style="{ minWidth: `${clientesDialogChartWidth}px` }")
          apexchart(
            type="bar",
            :options="clientesChartDialogOptions",
            :series="clientesDialogChartSeries",
            width="100%",
            height="520"
          )
        .panel-card__empty(v-else)
          span Nenhum dado encontrado para o período selecionado.
</template>

<script>
import SummaryCard from "@/components/dashboards/shared/SummaryCard";

const STATUS_CONFIGS = [
  { key: "aprovadas", chartLabel: "Aprovadas", chipLabel: "Aprovadas", color: "#2e7d32" },
  { key: "negadas", chartLabel: "Negadas", chipLabel: "Negadas", color: "#c62828" },
  { key: "canceladas", chartLabel: "Canceladas", chipLabel: "Canceladas", color: "#ef6c00" },
  {
    key: "sem_supervisor",
    chartLabel: "Sem Supervisor",
    chipLabel: "Sem Supervisor",
    color: "#5e35b1"
  },
  { key: "erro", chartLabel: "Erro", chipLabel: "Erro", color: "#6d4c41" },
  {
    key: "criadas",
    chartLabel: "Criadas",
    chipLabel: "Criadas",
    color: "#546e7a"
  }
];
const STATUS_KEYS = STATUS_CONFIGS.map(item => item.key);
const STATUS_CHART_COLORS = STATUS_CONFIGS.map(item => item.color);
const STATUS_COLOR_MAP = STATUS_CONFIGS.reduce((acc, item) => {
  acc[item.key] = item.color;
  return acc;
}, {});
const STATUS_LABEL_MAP = STATUS_CONFIGS.reduce((acc, item) => {
  acc[item.key] = item.chipLabel;
  return acc;
}, {});
const STATUS_KEY_ALIASES = {
  aprovada: "aprovadas",
  aprovadas: "aprovadas",
  negada: "negadas",
  negadas: "negadas",
  cancelada: "canceladas",
  canceladas: "canceladas",
  sem_supervisor: "sem_supervisor",
  semsupervisor: "sem_supervisor",
  erro: "erro",
  erros: "erro",
  criadas: "criadas",
  naomapeado: "criadas"
};
const INITIAL_CLIENTES_LIMIT = 10;
const CLIENTES_DIALOG_MIN_WIDTH = 1100;
const CLIENTES_DIALOG_ITEM_WIDTH = 140;
const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR");
const PERCENT_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});
const DECIMAL_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStatusKey(value) {
  if (!value) return "";
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

  return STATUS_KEY_ALIASES[normalized] || normalized;
}

function formatInteger(value) {
  return NUMBER_FORMATTER.format(Math.round(toNumber(value)));
}

function clienteTotal(item) {
  return STATUS_KEYS.reduce((acc, key) => acc + toNumber(item[key]), 0);
}

function sortByDetalhesCountDesc(items) {
  return toArray(items)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const diff =
        toArray((b.item && b.item.detalhes) || []).length -
        toArray((a.item && a.item.detalhes) || []).length;
      if (diff !== 0) return diff;
      return a.index - b.index;
    })
    .map(entry => entry.item);
}

function createClientesChartOptions(chartHeight, categories) {
  return {
    chart: {
      type: "bar",
      height: chartHeight,
      parentHeightOffset: 0,
      toolbar: { show: false },
      stacked: false
    },
    colors: STATUS_CHART_COLORS,
    stroke: { width: 0 },
    plotOptions: {
      bar: { borderRadius: 3, columnWidth: "72%" }
    },
    dataLabels: { enabled: false },
    grid: { borderColor: "#dfe7f5", strokeDashArray: 4 },
    legend: { show: true, position: "top", horizontalAlign: "center" },
    xaxis: {
      categories,
      labels: {
        rotate: -20,
        style: { colors: "#5f6b7a", fontSize: "12px" }
      }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      tickAmount: 6,
      labels: {
        formatter: value => formatInteger(value),
        style: { colors: "#5f6b7a", fontSize: "12px" }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: value => `${formatInteger(value)} solicitações` }
    }
  };
}

function createClientesChartSeries(items) {
  const normalizedItems = toArray(items);
  if (!normalizedItems.length) return [];
  const pluck = key => normalizedItems.map(item => toNumber(item[key]));
  const series = STATUS_CONFIGS.map(config => ({
    name: config.chartLabel,
    data: pluck(config.key)
  }));
  const hasAnyValue = series.some(s => s.data.some(v => v > 0));
  return hasAnyValue ? series : [];
}

export default {
  name: "BasesClientesTab",

  components: {
    SummaryCard
  },

  props: {
    totais: {
      type: Object,
      default: () => ({
        totalClientes: 0,
        mediaPorCliente: 0,
        taxaMediaAprovacao: 0,
        taxaAprovacao: 0,
        taxaNegacao: 0
      })
    },
    solicitacoesPorCliente: { type: Array, default: () => [] }
  },

  data() {
    return {
      search: "",
      expandedPanels: [],
      dialogs: { chart: false }
    };
  },

  computed: {
    summaryCards() {
      const cards = [
        {
          key: "media-por-cliente",
          title: "Média de solicitações por cliente",
          value: DECIMAL_FORMATTER.format(toNumber(this.totais.mediaPorCliente)),
          variant: "neutral"
        },
        {
          key: "taxa-media-aprovacao",
          title: "Taxa média de aprovação por cliente",
          value: `${PERCENT_FORMATTER.format(toNumber(this.totais.taxaMediaAprovacao))}%`,
          variant: "neutral"
        },
        {
          key: "taxa-aprovacao",
          title: "Taxa de solicitações aprovadas",
          value: `${PERCENT_FORMATTER.format(toNumber(this.totais.taxaAprovacao))}%`,
          variant: "success",
          icon: "mdi-check-circle",
          iconVariant: "success"
        },
        {
          key: "taxa-negacao",
          title: "Taxa de solicitações negadas",
          value: `${PERCENT_FORMATTER.format(toNumber(this.totais.taxaNegacao))}%`,
          variant: "danger",
          icon: "mdi-close-circle",
          iconVariant: "danger"
        }
      ];

      cards[0].key = "clientes-com-movimentacoes";
      cards[0].title = "Clientes com movimentações no período";
      cards[0].value = formatInteger(toNumber(this.totais.totalClientes));

      cards[1].key = "media-por-cliente";
      cards[1].title = "Média de solicitações por cliente";
      cards[1].value = DECIMAL_FORMATTER.format(toNumber(this.totais.mediaPorCliente));

      cards[2].title = "Taxa de aprovação";
      cards[3].title = "Taxa de negação";

      return cards;
    },

    detalhesHeaders() {
      return [
        { text: "Usuário", value: "usuario" },
        { text: "Supervisor", value: "supervisor" },
        { text: "Status", value: "status" },
        { text: "Empresa", value: "empresa" },
        { text: "Dispositivo", value: "dispositivo" },
        { text: "Última interação", value: "ultimaInteracao" }
      ];
    },

    filteredClientes() {
      const term = (this.search || "").trim().toLowerCase();
      const items = toArray(this.solicitacoesPorCliente).map(cliente => ({
        nome: cliente.nome || "",
        aprovadas: toNumber(cliente.aprovadas),
        negadas: toNumber(cliente.negadas),
        canceladas: toNumber(cliente.canceladas),
        sem_supervisor: toNumber(cliente.sem_supervisor),
        erro: toNumber(cliente.erro),
        criadas: toNumber(cliente.criadas),
        detalhes: toArray(cliente.detalhes)
      }));

      if (!term) return sortByDetalhesCountDesc(items);

      return sortByDetalhesCountDesc(
        items
          .map(cliente => {
            const nomeMatch = cliente.nome.toLowerCase().includes(term);
            const matchedDetalhes = cliente.detalhes.filter(detalhe =>
              ["usuario", "supervisor", "empresa", "dispositivo"].some(field =>
                String(detalhe[field] || "")
                  .toLowerCase()
                  .includes(term)
              )
            );
            if (nomeMatch) return cliente;
            if (matchedDetalhes.length) {
              return { ...cliente, detalhes: matchedDetalhes };
            }
            return null;
          })
          .filter(Boolean)
      );
    },

    clientesRankingItems() {
      return toArray(this.solicitacoesPorCliente)
        .map((cliente, index) => ({
          index,
          nome: cliente.nome || "",
          aprovadas: toNumber(cliente.aprovadas),
          negadas: toNumber(cliente.negadas),
          canceladas: toNumber(cliente.canceladas),
          sem_supervisor: toNumber(cliente.sem_supervisor),
          erro: toNumber(cliente.erro),
          criadas: toNumber(cliente.criadas)
        }))
        .filter(item => item.nome)
        .sort((a, b) => {
          const diff = clienteTotal(b) - clienteTotal(a);
          if (diff !== 0) return diff;
          return a.index - b.index;
        });
    },

    clientesPreviewItems() {
      return this.clientesRankingItems.slice(0, INITIAL_CLIENTES_LIMIT);
    },

    clientesCategories() {
      return this.clientesPreviewItems.map(cliente => cliente.nome);
    },

    clientesDialogCategories() {
      return this.clientesRankingItems.map(cliente => cliente.nome);
    },

    clientesChartSeries() {
      return createClientesChartSeries(this.clientesPreviewItems);
    },

    clientesDialogChartSeries() {
      return createClientesChartSeries(this.clientesRankingItems);
    },

    clientesChartOptions() {
      return createClientesChartOptions("100%", this.clientesCategories);
    },

    clientesChartDialogOptions() {
      const options = createClientesChartOptions(520, this.clientesDialogCategories);
      return {
        ...options,
        chart: {
          ...options.chart,
          animations: { enabled: false }
        },
        legend: {
          ...options.legend,
          position: "top",
          horizontalAlign: "left"
        },
        xaxis: {
          ...options.xaxis,
          labels: {
            ...options.xaxis.labels,
            show: true,
            rotate: -20,
            hideOverlappingLabels: false,
            showDuplicates: true,
            trim: false
          }
        }
      };
    },

    clientesDialogChartWidth() {
      return Math.max(
        CLIENTES_DIALOG_MIN_WIDTH,
        this.clientesDialogCategories.length * CLIENTES_DIALOG_ITEM_WIDTH
      );
    }
  },

  methods: {
    statusChipColor(status) {
      const key = normalizeStatusKey(status);
      return STATUS_COLOR_MAP[key] || "#6d4c41";
    },

    statusLabel(status) {
      const key = normalizeStatusKey(status);
      return STATUS_LABEL_MAP[key] || status || "-";
    },

    formatDateTime(value) {
      if (!value) return "-";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const year = parsed.getFullYear();
      const hour = String(parsed.getHours()).padStart(2, "0");
      const minute = String(parsed.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year}, ${hour}:${minute}`;
    }
  }
};
</script>

<style scoped>
.monitorsys-row {
  margin-left: -6px !important;
  margin-right: -6px !important;
}

.monitorsys-row > [class*="col-"] {
  padding: 6px !important;
}

.panel-card {
  border-color: #d9e2ef !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  border-radius: 18px 6px 18px 6px !important;
  display: flex;
  flex-direction: column;
  min-height: 420px;
  height: 420px;
  max-height: 420px;
}

.panel-card--details {
  min-height: 480px;
  height: 480px;
  max-height: 480px;
  overflow: hidden;
}

.panel-dialog {
  border-color: #d9e2ef !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.panel-card__header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.panel-card__header--dialog {
  flex: 0 0 auto;
  gap: 0;
  padding: 16px;
}

.panel-card__header--dialog .panel-card__title {
  flex: 0 1 auto;
}

.panel-card__title {
  color: #0a1f44;
  font-size: 16px;
  font-weight: 700;
  flex: 1 1 auto;
}

.panel-card__expand {
  height: 30px !important;
  min-width: 36px !important;
  padding: 0 8px !important;
}

.panel-card__content {
  flex: 1 1 0;
  min-height: 0;
  padding-bottom: 16px !important;
}

.panel-card__content--details {
  padding-top: 12px !important;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-width: thin;
}

.panel-card__content--details ::v-deep .v-expansion-panels {
  max-width: 100%;
}

.panel-card__content--details ::v-deep .v-expansion-panel-content__wrap {
  overflow-x: auto;
  overflow-y: hidden;
}

.monitorsys-details-table {
  max-width: 100%;
}

.monitorsys-details-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.panel-card__dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.panel-card__dialog-content--scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
}

.panel-card__dialog-content--chart {
  overflow-x: auto;
}

.panel-card__chart-canvas {
  min-height: 520px;
}

::v-deep .monitorsys-dialog-content .v-dialog {
  margin: 24px auto;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
}

.panel-card__empty {
  align-items: center;
  color: #5f6b7a;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.monitorsys-action-button {
  border-color: #c7d1dc !important;
  box-shadow: none !important;
  color: #6f7d8c !important;
  font-size: 11px !important;
  font-weight: 500;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
}

.monitorsys-action-button::before {
  background-color: transparent !important;
}

.monitorsys-action-button .v-icon {
  color: #748291 !important;
}

.monitorsys-search-field {
  flex: 0 0 280px;
  max-width: 280px;
}

.monitorsys-expansion-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.monitorsys-expansion-header__title {
  color: #0a1f44;
  font-size: 14px;
  font-weight: 600;
}

.monitorsys-expansion-header__badge {
  flex: 0 0 auto;
}

.monitorsys-details-table ::v-deep th {
  color: #5f6b7a !important;
  font-size: 12px !important;
  font-weight: 700 !important;
}

@media (max-width: 959px) {
  .panel-card {
    height: auto;
    min-height: 420px;
    max-height: none;
  }

  .panel-card--details {
    min-height: 420px;
    height: auto;
    max-height: none;
  }

  .panel-card__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .monitorsys-search-field {
    flex: 1 1 100%;
    max-width: 100%;
  }
}
</style>
